/**
 * Activity heartbeat — pings /api/activity/heartbeat so admins can see
 * who is currently active in the app on the /admin/access-activity
 * Live Visitors panel.
 *
 * Signals sent (per spec):
 *   * Page load (route change)
 *   * Every 60s while the tab is visible
 *   * Any user interaction (click / scroll / keypress), throttled to
 *     once per 30s
 *
 * The endpoint is public — anonymous share-link viewers also register
 * (with a locally-generated `session_id`). If any of admin / branch /
 * customer / driver auth is present in localStorage we attach that
 * identity so the visitor is labelled correctly in the admin panel.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'mr_activity_session_id';
const HEARTBEAT_MS = 60_000;
const INTERACTION_THROTTLE_MS = 30_000;

function readSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `sid-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

// Highest-priority identity wins so an admin logged into their own
// storefront preview still appears as "admin", not "customer".
function readIdentity() {
  const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
  const adminEmail = read('mr_admin_email');
  if (adminEmail && read('mr_admin_token')) {
    return { user_kind: 'admin', user_ref: adminEmail, user_label: adminEmail };
  }
  const branchName = read('mr_branch_name') || read('mr_branch_username');
  if (branchName && read('mr_branch_token')) {
    return { user_kind: 'branch', user_ref: branchName, user_label: branchName };
  }
  const driverName = read('mr_driver_name');
  const driverId = read('mr_driver_id');
  if ((driverName || driverId) && read('mr_driver_token')) {
    return { user_kind: 'driver', user_ref: driverId || driverName, user_label: driverName || driverId };
  }
  const custEmail = read('mr_customer_email');
  if (custEmail && read('mr_customer_token')) {
    return { user_kind: 'customer', user_ref: custEmail, user_label: custEmail };
  }
  return { user_kind: 'anonymous', user_ref: '', user_label: '' };
}

export default function useActivityHeartbeat() {
  const location = useLocation();
  const lastInteractionRef = useRef(0);

  useEffect(() => {
    const sid = readSessionId();
    if (!sid) return; // localStorage unavailable — skip silently.

    let cancelled = false;

    const send = () => {
      if (cancelled) return;
      if (document.hidden) return; // Don't ping while tab is backgrounded.
      const identity = readIdentity();
      const body = {
        session_id: sid,
        page: window.location.pathname + window.location.search,
        ...identity,
      };
      // fire-and-forget; failures are silent.
      try {
        fetch(`${API}/activity/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {});
      } catch {
        // network blocked / offline — ignore
      }
    };

    // 1) Immediate ping on mount + route change.
    send();

    // 2) 60s heartbeat.
    const iv = setInterval(send, HEARTBEAT_MS);

    // 3) Interaction ping (throttled).
    const onInteract = () => {
      const now = Date.now();
      if (now - lastInteractionRef.current < INTERACTION_THROTTLE_MS) return;
      lastInteractionRef.current = now;
      send();
    };
    const opts = { passive: true, capture: true };
    window.addEventListener('click', onInteract, opts);
    window.addEventListener('keydown', onInteract, opts);
    window.addEventListener('scroll', onInteract, opts);

    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('click', onInteract, opts);
      window.removeEventListener('keydown', onInteract, opts);
      window.removeEventListener('scroll', onInteract, opts);
    };
    // Re-arm on route change (immediate ping via `send()` above).
  }, [location.pathname, location.search]);
}
